import { StyleSheet } from 'aphrodite';
import {
	aceGray,
	antiFlashWhite,
	brandPrimary,
	darkGrayFontColor,
	grayIconFill,
	inputBorderColor,
	navigation,
	titles,
	white,
} from '../../styles/colors';
import { shadowSm } from '../../styles/shadows';
import { THEME } from '../Theme/theme';

export const s = StyleSheet.create({
	// Only a portal target for the caption Selects — it must not affect the calendar's own layout.
	portalHost: {
		display: 'contents',
	},
	calendar: {
		boxSizing: 'border-box',
		backgroundColor: 'transparent',
		padding: 12,
		'--cell-size': 32,
		':has(*) *': {
			boxSizing: 'border-box',
		},
	},
	calendarInCard: {
		backgroundColor: 'transparent',
	},
	calendarInPopover: {
		backgroundColor: 'transparent',
	},
	root: {
		width: 'fit-content',
	},
	months: {
		display: 'flex',
		gap: 16,
		flexDirection: 'column',
		position: 'relative',
		'@media (min-width: 768px)': {
			flexDirection: 'row',
		},
	},
	month: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		gap: 16,
	},
	nav: {
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		width: '100%',
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		justifyContent: 'space-between',
		// The bar spans the caption row, over the month/year Selects; only its arrows take clicks.
		pointerEvents: 'none',
	},
	navButton: {
		width: 'var(--cell-size)',
		height: 'var(--cell-size)',
		padding: 0,
		pointerEvents: 'auto',
		userSelect: 'none',
		color: brandPrimary,
		':is([aria-disabled])': {
			opacity: 0.5,
		},
	},
	monthCaption: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 'var(--cell-size)',
		width: '100%',
		paddingLeft: 'var(--cell-size)',
		paddingRight: 'var(--cell-size)',
	},
	dropdowns: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		fontSize: 14,
		fontWeight: THEME.typography.fontWeights.bold,
		justifyContent: 'center',
		height: 'var(--cell-size)',
		gap: 6,
	},
	captionSelectTrigger: {
		width: 'auto',
		height: 32,
		gap: 4,
		paddingLeft: 8,
		paddingRight: 4,
		border: `1px solid ${inputBorderColor}`,
		boxShadow: shadowSm,
		borderRadius: 8,
		backgroundColor: white,
		fontSize: 14,
		fontWeight: THEME.typography.fontWeights.bold,
		color: titles,
	},
	captionSelectContent: {
		// A year list runs to dozens of entries; cap it so the popup never outgrows the calendar.
		maxHeight: 280,
	},
	captionLabel: {
		userSelect: 'none',
		fontWeight: THEME.typography.fontWeights.bold,
	},
	captionLabelButton: {
		borderRadius: 6,
		paddingLeft: 8,
		paddingRight: 4,
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		fontSize: 14,
		height: 32,
		':has(*) > svg': {
			color: darkGrayFontColor,
			width: 14,
			height: 14,
		},
	},
	captionLabelText: {
		fontSize: 14,
	},
	table: {
		width: '100%',
		borderCollapse: 'collapse',
	},
	weekdays: {
		display: 'flex',
	},
	weekday: {
		color: darkGrayFontColor,
		borderRadius: 6,
		flex: 1,
		fontSize: 13,
		userSelect: 'none',
		fontWeight: THEME.typography.fontWeights.standard,
	},
	week: {
		display: 'flex',
		width: '100%',
		marginTop: 8,
	},
	weekNumberHeader: {
		userSelect: 'none',
		width: 'var(--cell-size)',
	},
	weekNumber: {
		fontSize: 13,
		userSelect: 'none',
		color: navigation,
	},
	day: {
		position: 'relative',
		width: '100%',
		height: '100%',
		padding: 0,
		textAlign: 'center',
		aspectRatio: '1 / 1',
		userSelect: 'none',
		":has(*) > .calendar-day-button[data-focused='true']": {
			position: 'relative',
			zIndex: 10,
			borderColor: grayIconFill,
			boxShadow: `0 0 0 3px ${grayIconFill}80`,
		},
		":first-child[data-selected='true'] button": {
			borderRadius: 6,
		},
	},
	dayFirstSelected: {
		':has(button)': {
			borderTopLeftRadius: 6,
			borderBottomLeftRadius: 6,
		},
	},
	dayLastSelected: {
		':has(button)': {
			borderTopRightRadius: 6,
			borderBottomRightRadius: 6,
		},
	},
	rangeStart: {
		borderTopLeftRadius: 6,
		borderBottomLeftRadius: 6,
		backgroundColor: antiFlashWhite,
	},
	rangeMiddle: {
		borderRadius: 0,
	},
	rangeEnd: {
		borderTopRightRadius: 6,
		borderBottomRightRadius: 6,
		backgroundColor: antiFlashWhite,
	},
	today: {
		backgroundColor: antiFlashWhite,
		color: titles,
		borderRadius: 6,
		':is([data-selected="true"])': {
			borderRadius: 0,
		},
	},
	todaySelected: {
		borderRadius: 0,
	},
	outside: {
		color: `oklch(55.6% 0 0)`,
	},
	outsideSelected: {
		color: navigation,
	},
	disabled: {
		color: titles,
		opacity: 0.5,
	},
	hidden: {
		visibility: 'hidden',
	},
	dayButton: {
		display: 'flex',
		aspectRatio: '1',
		width: 'auto',
		minWidth: 'var(--cell-size)',
		flexDirection: 'column',
		gap: 4,
		lineHeight: 1,
		fontWeight: 400,
		position: 'relative',
		zIndex: 1,
		borderRadius: 6,
		fontFamily: THEME.typography.fontFamilies.lexend,
	},
	dayButtonSelected: {
		backgroundColor: brandPrimary,
		color: white,
	},
	dayButtonRangeStart: {
		backgroundColor: brandPrimary,
		color: white,
		borderTopRightRadius: 6,
		borderBottomRightRadius: 6,
	},
	dayButtonRangeEnd: {
		backgroundColor: brandPrimary,
		color: white,
		borderTopLeftRadius: 6,
		borderBottomLeftRadius: 6,
	},
	dayButtonRangeMiddle: {
		backgroundColor: aceGray,
		color: titles,
		borderRadius: 0,
	},
	dayButtonFocused: {
		position: 'relative',
		zIndex: 10,
		borderColor: brandPrimary,
		boxShadow: `0 0 0 3px ${brandPrimary}33`,
	},
	dayButtonText: {
		fontSize: 12,
		opacity: 0.7,
	},
	weekNumberCell: {
		display: 'flex',
		width: 'var(--cell-size)',
		height: 'var(--cell-size)',
		alignItems: 'center',
		justifyContent: 'center',
		textAlign: 'center',
	},
	calendarDayButton: {
		display: 'flex',
		aspectRatio: '1 / 1',
		height: 'auto',
		width: '100%',
		minWidth: 'var(--cell-size)',
		flexDirection: 'column',
		gap: 4,
		lineHeight: 1,
		fontWeight: 400,
		':is([data-selected-single="true"])': {
			backgroundColor: brandPrimary,
			color: white,
		},
		':is([data-range-middle="true"])': {
			backgroundColor: antiFlashWhite,
			color: titles,
			borderRadius: 0,
		},
		':is([data-range-start="true"])': {
			backgroundColor: brandPrimary,
			color: white,
			borderRadius: 6,
		},
		':is([data-range-end="true"])': {
			backgroundColor: brandPrimary,
			color: white,
			borderRadius: 6,
		},
		':has(*) > span': {
			fontSize: 12,
			opacity: 0.7,
		},
	},
});
